/**
 * Meal planning data types
 * Converted from Go structs in api/data_model/model/meal_plans_llm.go
 */

import { OutgoingSearchResult } from "./search";
import { LLMMacros } from "./common";

export interface LLMMealPlanInputs {
    constraints: Record<string, any>;
}

export interface LLMMealPlanInputsWithMacros {
    constraints: Record<string, any>;
    macros: LLMMacros;
}

export interface ShoppingItem {
    name: string;
    portionQuantity: number;
    portionSize: string;
}

export interface LLMRecipeOutgoing {
    name: string;
    ingredients: OutgoingSearchResult[];
    preparation: string;
    macros: LLMMacros;
}

export interface LLMMealTimeSlotOutgoing {
    recipes: LLMRecipeOutgoing[];
}

export interface LLMMealPlanOutgoing {
    breakfast: LLMMealTimeSlotOutgoing;
    lunch: LLMMealTimeSlotOutgoing;
    dinner: LLMMealTimeSlotOutgoing;
    snack: LLMMealTimeSlotOutgoing;
    macros: LLMMacros;
}

export interface LLMMealPlanResultOutgoing {
    constraints: LLMMealPlanInputsWithMacros;
    mealPlanDays: LLMMealPlanOutgoing[];
    shoppingList: ShoppingItem[];
}

// Preview types
export interface RecipePreview {
    name: string;
    rejected: boolean;
}

export interface MealPreview {
    recipes: RecipePreview[];
}

export interface DayMealPlanPreview {
    breakfast: MealPreview;
    lunch: MealPreview;
    dinner: MealPreview;
    snack: MealPreview;
}

export interface MealPlanPreview {
    constraints: LLMMealPlanInputsWithMacros;
    mealPlanDays: DayMealPlanPreview[];
}


// Meal plan data interfaces aligned with Pydantic schema
  export interface ConstraintsWithMacros {
    constraints: Record<string, any>;
    macros?: LLMMacros;
  }
  
  // API response ingredient format
  export interface ApiIngredientPortion {
    weight: {
      unit: string;
      value: number;
    };
    name: string;
    quantity: number;
    suggestedQuantity?: number[];
  }
  
  export interface ApiNutritionPreview {
    portion: ApiIngredientPortion;
    calories: number;
    carbs: number;
    fat: number;
    protein: number;
    fiber?: number;
  }
  
  export interface ApiIngredient {
    type: string;
    displayName: string;
    shortName?: string;
    longName?: string;
    brandName?: string;
    nutritionPreview: ApiNutritionPreview;
    // Other API fields we don't need to use but might be present
    [key: string]: any;
  }
  
  // UI format for ingredients
  export interface RecipeIngredient {
    ingredientName: string;
    portionQuantity: number;
    portionSize: string;
    weightGrams: number;
    // Additional fields for frontend display
    calories?: number;
    carbs?: number;
    fat?: number;
    protein?: number;
    fiber?: number;
    // Keep the original API data for reference if needed
    apiData?: ApiIngredient;
  }
  
  export interface Recipe {
    name: string;
    ingredients: (RecipeIngredient | ApiIngredient)[];
    preparation: string;
    // API may include macros directly
    macros?: LLMMacros;
    // Additional fields for frontend
    rejected?: boolean;
    totalNutrition?: {
      calories: number;
      carbs: number;
      fat: number;
      protein: number;
      fiber?: number;
    };
  }
  
  export interface Meal {
    recipes: Recipe[];
    // Additional field for frontend
    totalNutrition?: {
      calories: number;
      carbs: number;
      fat: number;
      protein: number;
      fiber?: number;
    };
  }
  
  export interface DayMealPlan {
    breakfast: Meal;
    lunch: Meal;
    dinner: Meal;
    snack: Meal;
    // Day's macros (coming from the API)
    macros?: LLMMacros;
    // Additional field for frontend
    totalNutrition?: {
      calories: number;
      carbs: number;
      fat: number;
      protein: number;
      fiber?: number;
    };
  }
  
  export interface MealPlanData {
    constraints: ConstraintsWithMacros;
    mealPlanDays: DayMealPlan[];
  }
  
  // Helper functions to convert between API and UI formats
  export const convertApiIngredientToUi = (
    apiIngredient: ApiIngredient
  ): RecipeIngredient => {
    const { nutritionPreview, displayName } = apiIngredient;
    const {
      portion,
      calories,
      carbs,
      fat,
      protein,
      fiber = 0,
    } = nutritionPreview;
  
    return {
      ingredientName: displayName,
      portionQuantity: portion.quantity,
      portionSize: portion.name,
      weightGrams: portion.weight.value,
      calories,
      carbs,
      fat,
      protein,
      fiber,
      apiData: apiIngredient,
    };
  };
  
  export const isApiIngredient = (
    ingredient: any
  ): ingredient is ApiIngredient => {
    return (
      ingredient &&
      typeof ingredient === "object" &&
      "displayName" in ingredient &&
      "nutritionPreview" in ingredient
    );
  };
  
  // Detailed nutrition interfaces for full meal plan
  export interface IngredientWithNutrition {
    name: string;
    amount: string;
    calories: number;
    carbs: number;
    fat: number;
    protein: number;
  }
  
  export interface DetailedRecipe extends Omit<Recipe, "ingredients"> {
    ingredients: IngredientWithNutrition[];
    totalNutrition: {
      calories: number;
      carbs: number;
      fat: number;
      protein: number;
      fiber?: number;
    };
  }
  
  export interface DetailedMeal {
    recipes: DetailedRecipe[];
    totalNutrition: {
      calories: number;
      carbs: number;
      fat: number;
      protein: number;
      fiber?: number;
    };
  }
  
  export interface DetailedMealPlanDay {
    breakfast: DetailedMeal;
    lunch: DetailedMeal;
    dinner: DetailedMeal;
    snack: DetailedMeal;
    // Day's macros (coming from the API)
    macros?: LLMMacros;
    totalNutrition: {
      calories: number;
      carbs: number;
      fat: number;
      protein: number;
      fiber?: number;
    };
  }
  
  export interface FullMealPlanData {
    constraints: ConstraintsWithMacros;
    mealPlanDays: DetailedMealPlanDay[];
  }
  
  